/**
 * Body-type morph application (E2.4.S1, convention b) — drive the rig's glTF morph
 * targets from a character's fixed build (`CharacterAppearance.bodyType`).
 *
 * The three builds ARE the three morph targets (AVD-040/041 "no continuous
 * sliders"; appearance-customisation.md §2): a build engages exactly one target at
 * full influence, and `standard` is the base mesh (all targets zero). The rig is a
 * shared skeleton with these blendshapes (CHR-026 §4.3), so applying a build never
 * re-rigs and never touches collision — the 0.4 m footprint is body-INVARIANT
 * (appearance-customisation.md §6 / CHR-026 §5): appearance never reaches
 * `movement.ts`/`collision.ts`, so this is presentation-only, off the per-tick path.
 *
 * FRONTEND-LOCAL by design: the bodyType→target-name map is the client's rendering
 * convention, not a `shared/` constant (promotion = T3). A real rig delivered with
 * different target names is a re-import-time data/mapping fix (recorded on the
 * E2.5/E4.4 checklist), not a code change here.
 *
 * GRACEFUL by construction: a mesh with no morph targets (the AD-181 interim rig
 * carries none — CHR-027 morphs arrive with rig v2) is a NO-OP, and a mesh missing
 * the active build's target is a per-mesh no-op — never an error (spec §8). Node-
 * safe: operates on structural slices of `THREE.Object3D`/`THREE.Mesh`, no `three`
 * import, so the whole path runs under `node --test` (AD-120).
 */

import type { BodyType } from '@great-cloud/shared/client';

import { leftHeldGripFor, rightFistFor, type RightFist } from './heldGrip.js';

/**
 * Convention (b): body build → the morph target name engaged at influence 1.0.
 * `standard` = the base mesh (no target engaged — `null`). Closed over `BodyType`
 * so a new build would be a compile error here AND an owner rig decision (the enum
 * is closed by the rig, appearance-customisation.md §2), never a silent addition.
 */
export const MORPH_TARGET_FOR_BODY_TYPE: Readonly<Record<BodyType, string | null>> = {
  slight: 'slight',
  standard: null,
  sturdy: 'sturdy',
};

/**
 * The right hand's fists (q-grip-fist-inner-hand-see-through, tools/gltf-grip-transplant/grip-r-fist.mjs):
 * the fingers bent round a hilt (`grip-r-fist`, up to ~2.9 cm) or a thick grip (`grip-r-haft`).
 * AD-384's `grip-r` stays in the desktop body as its recorded lineage and is never driven: its
 * closed hand was an inside-out sleeve round an axis through the palm.
 */
export const RIGHT_HAND_GRIP_MORPH = 'grip-r-fist';
export const RIGHT_HAND_HAFT_MORPH = 'grip-r-haft';
const RIGHT_FIST_MORPHS: Readonly<Record<RightFist, string>> = {
  hilt: RIGHT_HAND_GRIP_MORPH,
  haft: RIGHT_HAND_HAFT_MORPH,
};

/**
 * The left hand's closed fist, sized for a bow riser (story `q-bow-left-hand-grip`; the bow is
 * left-lead, seated on LeftHand). Same contract as `grip-r`: a bounded POSITION target on the hand.
 */
export const LEFT_HAND_GRIP_MORPH = 'grip-l';

/**
 * The morph slice of a `THREE.Mesh`: the target-name→index dictionary and the
 * per-target influence array. Both are present only on meshes that carry morph
 * targets; absent ⇒ this node is not a morph mesh (no-op).
 */
export interface MorphMesh {
  morphTargetDictionary?: Record<string, number>;
  morphTargetInfluences?: number[];
}

/** The traverse slice of a `THREE.Object3D` — visits self + every descendant. */
export interface MorphRoot {
  traverse(callback: (node: unknown) => void): void;
}

/**
 * Apply a body build to a loaded rig root: for every morph mesh in the hierarchy,
 * zero all influences, then engage the build's target at 1.0 if the mesh carries
 * it. `standard` leaves everything at zero (the base mesh). Idempotent, and a no-op
 * for a target-less mesh or an absent target — so it is safe against the interim
 * untextured rig (no morphs) and any partially-morphed real rig.
 */
export function applyBodyMorph(root: MorphRoot, bodyType: BodyType): void {
  const activeName = MORPH_TARGET_FOR_BODY_TYPE[bodyType] ?? null;
  root.traverse((node) => {
    const mesh = node as MorphMesh;
    const dict = mesh.morphTargetDictionary;
    const influences = mesh.morphTargetInfluences;
    if (dict === undefined || influences === undefined) return; // not a morph mesh ⇒ no-op
    for (const bodyTarget of Object.values(MORPH_TARGET_FOR_BODY_TYPE)) {
      if (bodyTarget === null) continue;
      const bodyIndex = dict[bodyTarget];
      if (bodyIndex !== undefined && bodyIndex >= 0 && bodyIndex < influences.length) {
        influences[bodyIndex] = 0;
      }
    }
    if (activeName === null) return; // `standard` = base mesh, all zero
    const index = dict[activeName];
    if (index === undefined) return; // this mesh doesn't carry the build's target ⇒ no-op
    if (index >= 0 && index < influences.length) influences[index] = 1;
  });
}

/** Toggle one named morph target on every morph mesh under `root`; absent target ⇒ no-op. */
function setMorph(root: MorphRoot, target: string, active: boolean): void {
  root.traverse((node) => {
    const mesh = node as MorphMesh;
    const dict = mesh.morphTargetDictionary;
    const influences = mesh.morphTargetInfluences;
    if (dict === undefined || influences === undefined) return;
    const index = dict[target];
    if (index !== undefined && index >= 0 && index < influences.length) {
      influences[index] = active ? 1 : 0;
    }
  });
}

/**
 * Close the right hand on `fist` (default the hilt fist), or open it (`active` false), without
 * disturbing body or face targets. The other fist is always opened: at most one is ever on.
 */
export function applyRightHandGripMorph(
  root: MorphRoot,
  active: boolean,
  fist: RightFist = 'hilt',
): void {
  for (const [kind, target] of Object.entries(RIGHT_FIST_MORPHS))
    setMorph(root, target, active && kind === fist);
}

/** Toggle the authored left-hand (bow riser) grip without disturbing any other target. */
export function applyLeftHandGripMorph(root: MorphRoot, active: boolean): void {
  setMorph(root, LEFT_HAND_GRIP_MORPH, active);
}

/**
 * Close each hand that holds the drawn mainhand archetype `model` (rendered as `mesh`, when a
 * per-item mesh or skin has its own row): the row's right fist for a `HELD_GRIPS` row, `grip-l`
 * for a left-lead `LEFT_HELD_GRIPS` row (the bow), all open for none. One call keeps the fists
 * from ever disagreeing with the seat (`rigSocketPlacement` reads the same rows).
 */
export function applyHeldGripMorphs(
  root: MorphRoot,
  model: string | null,
  mesh: string | null = null,
): void {
  const fist = rightFistFor(mesh, model);
  applyRightHandGripMorph(root, fist !== null, fist ?? 'hilt');
  setMorph(root, LEFT_HAND_GRIP_MORPH, leftHeldGripFor(mesh, model) !== null);
}
